import React from 'react';
import { Trophy, Flag, UserPlus, Users, Database, Swords, Target } from 'lucide-react';
import { Tournament, Player } from '../types';

interface TabProps {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}

function Tab({ label, icon, isActive, onClick, disabled }: TabProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? 'bg-green-600 text-white'
          : disabled
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-white text-gray-600 hover:bg-gray-50'
      }`}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </button>
  );
}

interface NavigationProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isAdmin: boolean;
  selectedTournament: Tournament | null;
  isConnected: boolean;
  players?: Player[];
  tournaments?: Tournament[]; // Add tournaments prop
  onTournamentSelect?: (tournament: Tournament) => void; // Add tournament selection prop
}

export function Navigation({ 
  activeTab, 
  setActiveTab, 
  isAdmin, 
  selectedTournament,
  isConnected,
  players = [],
  tournaments = [], // Add tournaments parameter
  onTournamentSelect // Add tournament selection prop
}: NavigationProps) {
  // Only show relevant tabs based on tournament status
  const showRegistrationTab = selectedTournament?.status === 'registration' || 
                             tournaments.some(t => t.status === 'registration');
  const showMatchPlayTab = selectedTournament?.status === 'active' && selectedTournament.format === 'match';
  
  // Show leaderboard tab if:
  // 1. There's a selected tournament with valid status, OR
  // 2. There are any tournaments available (fallback)
  // AND not on special tabs
  const showLeaderboardTab = (
    (selectedTournament?.status === 'registration' || 
     selectedTournament?.status === 'active' || 
     selectedTournament?.status === 'completed' ||
     tournaments.length > 0) // Show if any tournaments exist
  ) && activeTab !== 'hc-standing' && activeTab !== 'tx-cup';
  
  const showImportTab = isAdmin && selectedTournament?.status !== 'completed';

  // Check if all players have completed their rounds
  const hasCompletedRound = (player: Player) => {
    if (!player.scores) return false;
    const completedHoles = new Set(player.scores.map(s => s.hole));
    return completedHoles.size === 18;
  };

  // Determine if scoring should be disabled
  const isScoringDisabled = 
    selectedTournament?.status === 'completed' || 
    (players.length > 0 && players.every(player => hasCompletedRound(player)));

  // If scoring is disabled and we're on the scoring tab, redirect to leaderboard
  React.useEffect(() => {
    if (isScoringDisabled && activeTab === 'scoring') {
      setActiveTab('leaderboard');
    }
  }, [isScoringDisabled, activeTab]);

  return (
    <nav className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      {/* Always show Tournaments tab for admins */}
      {isAdmin && (
        <Tab
          label="Tournaments"
          icon={<Users className="w-4 h-4" />}
          isActive={activeTab === 'tournaments'}
          onClick={() => setActiveTab('tournaments')}
        />
      )}

      {/* Import/Setup tab - only for admins when tournament is in draft */}
      {showImportTab && (
        <Tab
          label="Setup"
          icon={<Database className="w-4 h-4" />}
          isActive={activeTab === 'import'}
          onClick={() => setActiveTab('import')}
        />
      )}

      {/* Registration tab - only during registration period */}
      {showRegistrationTab && activeTab !== 'hc-standing' && activeTab !== 'tx-cup' && (
        <Tab
          label="Registration"
          icon={<UserPlus className="w-4 h-4" />}
          isActive={activeTab === 'registration'}
          onClick={() => {
            // Find and select the registration tournament
            const registrationTournament = tournaments.find(t => t.status === 'registration');
            console.log('Registration tab clicked. Available tournaments:', tournaments.map(t => ({ name: t.name, status: t.status, type: t.tournamentType })));
            console.log('Found registration tournament:', registrationTournament);
            if (onTournamentSelect && registrationTournament) {
              onTournamentSelect(registrationTournament);
            }
            setActiveTab('registration');
          }}
        />
      )}

      {/* Scoring tab - only when tournament is active and format is stroke play */}
      {selectedTournament?.status === 'active' && selectedTournament.format === 'stroke' && (
        <Tab
          label="Scoring"
          icon={<Flag className="w-4 h-4" />}
          isActive={activeTab === 'scoring'}
          onClick={() => setActiveTab('scoring')}
          disabled={isScoringDisabled}
        />
      )}

      {/* Match Play tab - only for admins (legacy system, replaced by Matchplay v2) */}
      {showMatchPlayTab && isAdmin && (
        <Tab
          label="Match Play"
          icon={<Swords className="w-4 h-4" />}
          isActive={activeTab === 'matchplay'}
          onClick={() => setActiveTab('matchplay')}
        />
      )}

      {/* Matchplay v2 tab - New professional matchplay system (available to all users) */}
      <Tab
        label="Matchplay v2"
        icon={<Target className="w-4 h-4" />}
        isActive={activeTab === 'matchplay-v2'}
        onClick={() => setActiveTab('matchplay-v2')}
      />

      {/* Leaderboard tab - show for all tournament statuses except hc-standing and tx-cup */}
      {showLeaderboardTab && (
        <Tab
          label="Leaderboard"
          icon={<Trophy className="w-4 h-4" />}
          isActive={activeTab === 'leaderboard'}
          onClick={() => setActiveTab('leaderboard')}
        />
      )}
    </nav>
  );
}